import { ProtectorType } from 'core/constants';

import { FormBuilderMode } from './mode';
import { ONE_SITE_QUESTION, siteQuestions } from './siteQuestion';
import { AuditFormModel, AuditSection, sectionTitleId } from './types';

type BlockerRule =
  | 'no-template-name'
  | 'no-questions'
  | 'empty-section'
  | 'blank-question-text'
  | 'lookup-without-type'
  | 'duplicate-section-name'
  | 'too-many-site-questions';

export type Blocker = { rule: BlockerRule; message: string };

const sectionName = (section: AuditSection, index: number): string => section.title.trim() || `Section ${index + 1}`;

const questionName = (description: string): string => description.trim() || 'Untitled question';

/** One entry per offender, in canvas order: named before Publish rather than coming back as a field-path 400 after it. */
export const publishBlockers = (model: AuditFormModel, name: string, mode: FormBuilderMode): Blocker[] => {
  const blockers: Blocker[] = [];

  if (name.trim() === '') {
    blockers.push({ rule: 'no-template-name', message: 'Give the template a name before publishing.' });
  }

  const questionCount = model.sections.reduce((total, section) => total + section.questions.length, 0);
  if (questionCount === 0) {
    // One message, not one per empty section.
    blockers.push({ rule: 'no-questions', message: 'Add at least one question before publishing.' });
    return blockers;
  }

  model.sections.forEach((section, index) => {
    const label = sectionName(section, index);

    if (section.questions.length === 0) {
      blockers.push({
        rule: 'empty-section',
        message: `“${label}” has no questions. Add one or delete the section.`,
      });
    }

    section.questions.forEach((question) => {
      if (question.description.trim() === '') {
        blockers.push({
          rule: 'blank-question-text',
          message: `A question in “${label}” has no question text. Add it before publishing.`,
        });
      }

      // `validate_lookup` filters on `entity_type__key=None`, so no answer can ever match.
      if (question.questionType === ProtectorType.Lookup && !question.lookupType) {
        blockers.push({
          rule: 'lookup-without-type',
          message: `“${questionName(question.description)}” needs a record type before publishing.`,
        });
      }
    });
  });

  // Blank titles are the server's to refuse, and two of them are not a naming clash.
  const seenTitles = new Map<string, string>();
  const duplicated = new Map<string, string>();

  model.sections.forEach((section) => {
    const id = sectionTitleId(section.title);
    if (id === '') return;

    const first = seenTitles.get(id);
    if (first === undefined) seenTitles.set(id, section.title.trim());
    else duplicated.set(id, first);
  });

  duplicated.forEach((title) => {
    blockers.push({
      rule: 'duplicate-section-name',
      message: `More than one section is called “${title}”. Rename one before publishing.`,
    });
  });

  if (siteQuestions(mode, model.sections).length > 1) {
    blockers.push({ rule: 'too-many-site-questions', message: `${ONE_SITE_QUESTION} Remove the other one.` });
  }

  return blockers;
};

/** `many` reads as "<count> <text>"; `one` is a whole line; a rule that fires at most once has no `many`.
 *  Neither repeats the lead line's "before publishing". */
const RULE_SUMMARY: Record<BlockerRule, { one: string; many?: string }> = {
  'no-template-name': { one: 'Give the template a name.' },
  'no-questions': { one: 'Add at least one question.' },
  'empty-section': { one: '1 section has no questions.', many: 'sections have no questions.' },
  'blank-question-text': { one: '1 question has no question text.', many: 'questions have no question text.' },
  'lookup-without-type': {
    one: '1 lookup question needs a record type.',
    many: 'lookup questions need a record type.',
  },
  'duplicate-section-name': {
    one: '1 section name is used more than once.',
    many: 'section names are used more than once.',
  },
  'too-many-site-questions': { one: 'Keep only one Site question.' },
};

/** One line per rule with a count, so the tooltip stays a few lines however big the template. */
export const summarisePublishBlockers = (blockers: Blocker[]): string[] => {
  // A lone blocker keeps its full sentence, which already names the fix.
  if (blockers.length <= 1) return blockers.map((blocker) => blocker.message);

  const counted = new Map<BlockerRule, number>();
  blockers.forEach(({ rule }) => counted.set(rule, (counted.get(rule) ?? 0) + 1));

  return [...counted.entries()].map(([rule, count]) => {
    const { one, many } = RULE_SUMMARY[rule];
    return count === 1 || !many ? one : `${count} ${many}`;
  });
};
